import { supabase } from '../lib/supabase';

export const businessService = {
  // Get business profile by user ID
  async getBusinessByUserId(userId) {
    const { data, error } = await supabase?.from('business_profiles')?.select('*')?.eq('user_id', userId)?.single();
    
    if (error) {
      if (error?.code === 'PGRST116') return null; // No business found
      throw error;
    }
    
    return this.convertToCamelCase(data);
  },

  // Get business profile by ID
  async getBusinessById(businessId) {
    const { data, error } = await supabase?.from('business_profiles')?.select('*')?.eq('id', businessId)?.single();
    
    if (error) throw error;
    return this.convertToCamelCase(data);
  },

  // Get all active businesses
  async getAllBusinesses(filters = {}) {
    let query = supabase?.from('business_profiles')?.select('*')?.eq('is_active', true)?.eq('is_verified', true);

    if (filters?.category) {
      query = query?.eq('category', filters?.category);
    }

    if (filters?.city) {
      query = query?.eq('city', filters?.city);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data?.map(business => this.convertToCamelCase(business));
  },

  // Create business profile
  async createBusiness(userId, businessData) {
    const dbData = {
      user_id: userId,
      legal_name: businessData?.legalName,
      commercial_name: businessData?.commercialName,
      nit: businessData?.nit,
      category: businessData?.category,
      subcategory: businessData?.subcategory,
      description: businessData?.description,
      address: businessData?.address,
      city: businessData?.city,
      department: businessData?.department,
      neighborhood: businessData?.neighborhood,
      contact_email: businessData?.contactEmail,
      contact_phone: businessData?.contactPhone,
      whatsapp_number: businessData?.whatsappNumber,
      website_url: businessData?.websiteUrl,
      facebook_url: businessData?.facebookUrl,
      instagram_url: businessData?.instagramUrl,
      twitter_url: businessData?.twitterUrl,
      logo_url: businessData?.logoUrl,
      cover_image_url: businessData?.coverImageUrl,
      discount_percentage: businessData?.discountPercentage || 0
    };

    const { data, error } = await supabase?.from('business_profiles')?.insert(dbData)?.select()?.single();
    
    if (error) throw error;
    return this.convertToCamelCase(data);
  },

  // Update business profile
  async updateBusiness(businessId, updates) {
    const dbUpdates = {
      commercial_name: updates?.commercialName,
      description: updates?.description,
      address: updates?.address,
      city: updates?.city,
      department: updates?.department,
      neighborhood: updates?.neighborhood,
      contact_email: updates?.contactEmail,
      contact_phone: updates?.contactPhone,
      whatsapp_number: updates?.whatsappNumber,
      website_url: updates?.websiteUrl,
      facebook_url: updates?.facebookUrl,
      instagram_url: updates?.instagramUrl,
      twitter_url: updates?.twitterUrl,
      logo_url: updates?.logoUrl,
      cover_image_url: updates?.coverImageUrl,
      discount_percentage: updates?.discountPercentage,
      updated_at: new Date()?.toISOString()
    };

    // Remove undefined values
    Object.keys(dbUpdates)?.forEach(key => 
      dbUpdates?.[key] === undefined && delete dbUpdates?.[key]
    );

    const { data, error } = await supabase?.from('business_profiles')?.update(dbUpdates)?.eq('id', businessId)?.select()?.single();
    
    if (error) throw error;
    return this.convertToCamelCase(data);
  },

  // Get business gallery images
  async getGalleryImages(businessId) {
    const { data, error } = await supabase?.from('business_gallery')?.select('*')?.eq('business_id', businessId)?.order('display_order', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(img => ({
      id: img?.id,
      businessId: img?.business_id,
      imageUrl: img?.image_url,
      altText: img?.alt_text,
      displayOrder: img?.display_order
    }));
  },

  // Get business operating hours
  async getOperatingHours(businessId) {
    const { data, error } = await supabase?.from('business_hours')?.select('*')?.eq('business_id', businessId);
    
    if (error) throw error;
    
    return data?.map(hour => ({
      id: hour?.id,
      businessId: hour?.business_id,
      dayOfWeek: hour?.day_of_week,
      openTime: hour?.open_time,
      closeTime: hour?.close_time,
      isClosed: hour?.is_closed
    }));
  },

  // Helper: Convert snake_case to camelCase
  convertToCamelCase(data) {
    if (!data) return null;
    
    return {
      id: data?.id,
      userId: data?.user_id,
      legalName: data?.legal_name,
      commercialName: data?.commercial_name,
      nit: data?.nit,
      category: data?.category,
      subcategory: data?.subcategory,
      description: data?.description,
      address: data?.address,
      city: data?.city,
      department: data?.department,
      neighborhood: data?.neighborhood,
      contactEmail: data?.contact_email,
      contactPhone: data?.contact_phone,
      whatsappNumber: data?.whatsapp_number,
      websiteUrl: data?.website_url,
      facebookUrl: data?.facebook_url,
      instagramUrl: data?.instagram_url,
      twitterUrl: data?.twitter_url,
      logoUrl: data?.logo_url,
      coverImageUrl: data?.cover_image_url,
      qrCode: data?.qr_code,
      discountPercentage: data?.discount_percentage,
      isActive: data?.is_active,
      isVerified: data?.is_verified,
      totalScans: data?.total_scans,
      uniqueCustomers: data?.unique_customers,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  }
};
